import React, { useState, useEffect } from 'react';
import { useSafeThemeClasses } from '@/shared/theme';
import { adminApiService } from '@/shared/services/adminApiService';
import type { AdminUser } from '@/shared/types/admin';
import { logger } from '@/shared/utils';
import toast from 'react-hot-toast';
import { dateUtils } from '@/shared/utils';
import { useConfirmDialog } from '@/shared/components/ui';
import { useUserState } from '@/features/user/operations/UserStateProvider';

const AdminUserManagementPage: React.FC = () => {
  const theme = useSafeThemeClasses();
  const { user: viewer } = useUserState();
  const isGod = viewer?.is_god === true;
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState<Partial<AdminUser>>({});
  const { confirm, ConfirmDialog } = useConfirmDialog();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await adminApiService.getUsers();
      if (response.success && response.data) {
        setUsers(response.data.users || []);
      } else {
        toast.error(response.error?.message || 'Failed to load users');
        logger.error('Failed to load users', 'AdminUserManagementPage', { error: response.error });
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Network error loading users', 'AdminUserManagementPage', { error });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId: string) => {
    try {
      const response = await adminApiService.approveUser(userId);
      if (response.success && response.data) {
        toast.success('User approved successfully');
        await loadUsers();
      } else {
        toast.error(response.error?.message || 'Failed to approve user');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to approve user', 'AdminUserManagementPage', { error });
    }
  };

  const handleUpdate = async (userId: string) => {
    try {
      const response = await adminApiService.updateUser(userId, editForm);
      if (response.success && response.data) {
        toast.success('User updated successfully');
        setEditingUser(null);
        setEditForm({});
        await loadUsers();
      } else {
        toast.error(response.error?.message || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to update user', 'AdminUserManagementPage', { error });
    }
  };

  const handlePromoteToAdmin = async (userId: string) => {
    const ok = await confirm({
      title: 'Promote to admin?',
      message: 'This user will gain full admin access.',
      confirmLabel: 'Promote',
    });
    if (!ok) {
      return;
    }
    try {
      const response = await adminApiService.updateUserRole(userId, 'admin');
      if (response.success && response.data) {
        toast.success('User promoted to admin successfully');
        await loadUsers();
      } else {
        toast.error(response.error?.message || 'Failed to promote user');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to promote user', 'AdminUserManagementPage', { error });
    }
  };

  const handleDemoteToUser = async (userId: string) => {
    const ok = await confirm({
      title: 'Demote to standard user?',
      message: 'This admin will lose admin access immediately.',
      confirmLabel: 'Demote',
    });
    if (!ok) {
      return;
    }
    try {
      const response = await adminApiService.updateUserRole(userId, 'user');
      if (response.success && response.data) {
        toast.success('Admin demoted to standard user');
        await loadUsers();
      } else {
        toast.error(response.error?.message || 'Failed to demote user');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to demote user', 'AdminUserManagementPage', { error });
    }
  };

  const handleDisable = async (userId: string) => {
    const ok = await confirm({
      title: 'Disable this user?',
      message: 'They will not be able to log in until re-enabled.',
      confirmLabel: 'Disable',
    });
    if (!ok) {
      return;
    }
    try {
      const response = await adminApiService.deactivateUser(userId);
      if (response.success && response.data) {
        toast.success('User disabled successfully');
        await loadUsers();
      } else {
        toast.error(response.error?.message || 'Failed to disable user');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to disable user', 'AdminUserManagementPage', { error });
    }
  };

  const handleEnable = async (userId: string) => {
    try {
      const response = await adminApiService.activateUser(userId);
      if (response.success && response.data) {
        toast.success('User enabled successfully');
        await loadUsers();
      } else {
        toast.error(response.error?.message || 'Failed to enable user');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to enable user', 'AdminUserManagementPage', { error });
    }
  };

  const handleGenerateResetLink = async (userId: string) => {
    try {
      const response = await adminApiService.generatePasswordResetLink(userId);
      if (response.success && response.data) {
        await navigator.clipboard.writeText(response.data.reset_url);
        toast.success('Reset link copied to clipboard. It expires in 24 hours.');
      } else {
        toast.error(response.error?.message || 'Failed to generate reset link');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to generate reset link', 'AdminUserManagementPage', { error });
    }
  };

  const handleDelete = async (userId: string) => {
    const ok = await confirm({
      title: 'Delete this user?',
      message: 'This permanently deletes the account and cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
      requireTypedWord: 'DELETE',
    });
    if (!ok) {
      return;
    }
    try {
      const response = await adminApiService.deleteUser(userId);
      if (response.success) {
        toast.success('User deleted successfully');
        await loadUsers();
      } else {
        toast.error(response.error?.message || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
      logger.error('Failed to delete user', 'AdminUserManagementPage', { error });
    }
  };

  const pendingUsers = users.filter(u => u.requires_approval && !u.is_active);
  // Show all users (active and inactive) so we can enable/disable them
  const allUsers = users.filter(u => !u.requires_approval || u.is_active);

  if (loading) {
    return (
      <div className={`${theme.card} p-8 text-center`}>
        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${theme.textAccent}`}></div>
        <p className={`mt-4 ${theme.textSecondary}`}>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog />
      <h2 className={`text-2xl font-bold mb-6 ${theme.textHeading} tracking-tight`}>User Management</h2>

      {/* Pending Approvals Section - Glass Morphism */}
      {pendingUsers.length > 0 && (
        <div className={`rounded-3xl p-8 mb-6 transition-all duration-300 ${theme.card}`}>
          <h3 className={`text-xl font-bold mb-2 ${theme.textHeading} tracking-tight`}>Pending Approvals</h3>
          <p className={`text-sm mb-6 ${theme.textSecondary}`}>Users waiting for approval</p>
          <div className="space-y-3">
            {pendingUsers.map(user => (
              <div
                key={user.id}
                className={`rounded-lg p-5 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 transition-all duration-300 border border-opacity-10 ${theme.textPrimary}`}
              >
                <div>
                  <p className={`font-medium ${theme.textPrimary}`}>
                    {user.first_name && user.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user.username}
                  </p>
                  <p className={`text-sm ${theme.textSecondary}`}>{user.email}</p>
                  <p className={`text-xs ${theme.textMuted}`}>
                    Registered: {dateUtils.format(user.created_at, 'SHORT')}
                  </p>
                </div>
                <button
                  onClick={() => handleApprove(user.id)}
                  className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 self-start sm:self-auto ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:scale-105 hover:shadow-lg active:scale-95`}
                >
                  Approve
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Users Section - Glass Morphism */}
      <div className={`rounded-3xl p-8 transition-all duration-300 ${theme.card}`}>
        <h3 className={`text-xl font-bold mb-6 ${theme.textHeading} tracking-tight`}>All Users</h3>
        <div className="space-y-3">
          {allUsers.length === 0 ? (
            <p className={`text-center py-8 ${theme.textMuted}`}>No users found</p>
          ) : (
            allUsers.map(user => (
              <div
                key={user.id}
                className={`rounded-lg p-5 transition-all duration-300 border border-opacity-10 ${
                  editingUser?.id === user.id ? 'ring-2 ring-opacity-50 ring-blue-500' : ''
                } ${theme.textPrimary}`}
              >
                {editingUser?.id === user.id ? (
                  <div className="space-y-3">
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>
                        First Name
                      </label>
                      <input
                        type="text"
                        value={editForm.first_name || ''}
                        onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={editForm.last_name || ''}
                        onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={editForm.email || ''}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg ${theme.input} ${theme.textPrimary}`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${theme.textLabel}`}>
                        Bio
                      </label>
                      <textarea
                        value={editForm.bio || ''}
                        onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                        className={`w-full px-3 py-2 rounded-lg ${theme.textarea} ${theme.textPrimary}`}
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-3 pt-4 border-t border-opacity-20">
                      <button
                        onClick={() => handleUpdate(user.id)}
                        className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:scale-105 hover:shadow-lg active:scale-95`}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingUser(null);
                          setEditForm({});
                        }}
                        className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 ${theme.buttonGhost} hover:scale-105 active:scale-95`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className={`font-medium ${theme.textPrimary}`}>
                          {user.first_name && user.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : user.username}
                        </p>
                        {user.is_admin && (
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${theme.badge} ${theme.textAccent}`}>
                            Admin
                          </span>
                        )}
                        {!user.is_active && (
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${theme.alertWarning}`}>
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${theme.textSecondary}`}>{user.email}</p>
                      <p className={`text-xs ${theme.textMuted}`}>
                        {user.approved_at && (
                          <span>Approved: {dateUtils.format(user.approved_at, 'SHORT')}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {!user.is_admin && (
                        <button
                          onClick={() => handlePromoteToAdmin(user.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:scale-105 hover:shadow-lg active:scale-95`}
                          title="Promote to Admin"
                        >
                          Promote
                        </button>
                      )}
                      {/* Demoting an admin back to a standard user is god-only -
                          the backend 404s this same call for a regular admin,
                          so the button is hidden rather than shown-then-failing. */}
                      {user.is_admin && !user.is_god && isGod && (
                        <button
                          onClick={() => handleDemoteToUser(user.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 ${theme.buttonSecondary} hover:scale-105 hover:shadow-lg active:scale-95`}
                          title="Demote to standard user"
                        >
                          Demote
                        </button>
                      )}
                      {!user.is_admin && user.is_active && (
                        <button
                          onClick={() => handleDisable(user.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 ${theme.buttonSecondary} hover:scale-105 hover:shadow-lg active:scale-95`}
                          title="Disable User (Temporary)"
                        >
                          Disable
                        </button>
                      )}
                      {!user.is_admin && !user.is_active && (
                        <button
                          onClick={() => handleEnable(user.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 ${theme.buttonPrimary} ${theme.buttonTextPrimary} hover:scale-105 hover:shadow-lg active:scale-95`}
                          title="Enable User"
                        >
                          Enable
                        </button>
                      )}
                      <button
                        onClick={() => handleGenerateResetLink(user.id)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 ${theme.buttonSecondary} hover:scale-105 hover:shadow-lg active:scale-95`}
                        title="Copy a password reset link for this user"
                      >
                        Reset Link
                      </button>
                      {!user.is_admin && (
                        <button
                          onClick={() => handleDelete(user.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 ${theme.alertError} text-white hover:scale-105 hover:shadow-lg active:scale-95`}
                          title="Delete User (Permanent)"
                        >
                          Delete
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditingUser(user);
                          setEditForm({
                            first_name: user.first_name,
                            last_name: user.last_name,
                            email: user.email,
                            bio: user.bio,
                          });
                        }}
                        className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 ${theme.buttonGhost} hover:scale-105 active:scale-95`}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUserManagementPage;

